<template>
    <div class="container">
        <div class="row justify-content-center">
            <div class="col-12 col-xl-11">
                <br />
                <!-- Breadcrumbs -->
                <div class="row">
                    <div class="col-12">
                        <router-link :to="{ name: 'ShareLinks', params: { id: caseId } }">Share Links</router-link>
                        <span class="text-secondary mx-2">/</span>
                        <span>{{ shareLink.name }}</span>
                    </div>
                </div>
                <br />
                <br />

                <!-- Report Config Controls -->
                <div class="row">
                    <div class="col-12 col-lg-10 col-xl-7">
                        <div class="row align-items-center mb-3">
                            <div class="col-12 col-sm-3">Report Type</div>
                            <div class="col-12 col-sm-9">
                                <report-type-selector
                                    v-model="reportQuery.reportType"
                                    :include-session-list="true"
                                ></report-type-selector>
                            </div>
                        </div>
                        <div
                            v-if="['choiceResponse', 'textResponse'].includes(reportQuery.reportType)"
                            class="row d-flex align-items-center mb-3"
                        >
                            <div class="col-12 col-sm-3">Inquiry</div>
                            <div class="col-12 col-sm-9">
                                <report-panel-selector
                                    v-model="reportQuery.panelID"
                                    :options="selectedReportPanelOptions"
                                ></report-panel-selector>
                            </div>
                        </div>
                        <base-form
                            v-model="reportQuery"
                            :fields="reportFields"
                            :inline-style="true"
                            ref="reportQueryForm"
                        ></base-form>
                        <button
                            class="btn btn-primary btn-sm float-right"
                            :disabled="!reportQueryValid"
                            @click="fetchReportData()"
                        >
                            Submit
                        </button>
                    </div>
                </div>
                <br />
                <hr />
                <br />
                <!-- Session List -->
                <div v-if="reportQuery.reportType == 'sessionList'">
                    <session-list-report
                        ref="sessionListReport"
                        :organization-id="currentOrganization.id"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :share-link-id="linkId"
                    ></session-list-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'caseCompletion'">
                    <completion-report
                        ref="completionReport"
                        :group-id="reportQuery.groupID"
                        :organization-id="currentOrganization.id"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :share-link-id="linkId"
                    ></completion-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'choiceResponse'">
                    <choice-response-report
                        ref="choiceResponseReport"
                        :group-id="reportQuery.groupID"
                        :organization-id="currentOrganization.id"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :panel-id="reportQuery.panelID"
                        :first-response-only="reportQuery.firstResponseOnly"
                        :share-link-id="linkId"
                    ></choice-response-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'textResponse'">
                    <text-response-report
                        ref="textResponseReport"
                        :group-id="reportQuery.groupID"
                        :organization-id="currentOrganization.id"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :panel-id="reportQuery.panelID"
                        :share-link-id="linkId"
                    ></text-response-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'timerPerformance'">
                    <timer-performance-report
                        ref="timerPerformanceReport"
                        :group-id="reportQuery.groupID"
                        :organization-id="currentOrganization.id"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :timer-id="reportQuery.timerID"
                        :share-link-id="linkId"
                    ></timer-performance-report>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import BaseForm from "@/components/BaseForm"
import CompletionReport from "@/components/reports/CompletionReport"
import ChoiceResponseReport from "@/components/reports/ChoiceResponseReport"
import TextResponseReport from "@/components/reports/TextResponseReport"
import TimerPerformanceReport from "@/components/reports/TimerPerformanceReport"
import ReportTypeSelector from "@/components/reports/ReportTypeSelector"
import ReportPanelSelector from "@/components/reports/ReportPanelSelector"
import SessionListReport from "@/components/reports/SessionListReport"
import http from "@/axios.js"

export default {
    name: "LinkReports",
    components: {
        BaseForm,
        CompletionReport,
        ChoiceResponseReport,
        TextResponseReport,
        TimerPerformanceReport,
        SessionListReport,
        ReportTypeSelector,
        ReportPanelSelector,
    },
    data() {
        return {
            reportQuery: {
                reportType: "sessionList",
                startDate: null,
                endDate: null,
                panelID: null,
                timerID: null,
                firstResponseOnly: false,
            },
            panels: [],
            timers: [],
            shareLink: {
                name: "",
            },
            reportIsLoaded: false,
            timersLoading: false,
            panelsLoading: false,
        }
    },
    computed: {
        caseId() {
            return Number(this.$route.params.id)
        },
        linkId() {
            return Number(this.$route.params.linkId)
        },
        currentOrganization() {
            return this.$store.getters.currentOrganization
        },
        reportFields() {
            let vm = this
            return [
                {
                    name: "Timer",
                    key: "timerID",
                    required: true,
                    type: "search",
                    options: this.timers,
                    labelField: "label",
                    valueField: "id",
                    searchFields: ["label"],
                    maxItems: 1,
                    placeholder: "Select a learning experience, then select a timer",
                    hidden(value) {
                        return value.reportType != "timerPerformance"
                    },
                    loading: vm.timersLoading,
                },
                {
                    name: "Start Date",
                    key: "startDate",
                    type: "date",
                    required: true,
                },
                {
                    name: "End Date",
                    key: "endDate",
                    type: "date",
                    required: true,
                },
                {
                    name: "Only include each learner's first response",
                    key: "firstResponseOnly",
                    type: "checkbox",
                    required: false,
                    hidden(value) {
                        return value.reportType != "choiceResponse"
                    },
                    positionIndent: true,
                },
            ]
        },
        reportType() {
            return this.reportQuery.reportType
        },
        selectedReportPanelOptions() {
            let applicablePanelTypes = []
            if (this.reportQuery.reportType == "choiceResponse") {
                applicablePanelTypes = ["multichoice", "multiselect", "singleselect", "image_hotspots"]
            } else if (this.reportQuery.reportType == "textResponse") {
                applicablePanelTypes = ["textarea"]
            }
            return this.panels.filter((p) => applicablePanelTypes.includes(p.panel_type))
        },
        inquiryReportSelected() {
            return ["choiceResponse", "textResponse"].includes(this.reportQuery.reportType)
        },
        // Determines whether the report query is valid.
        reportQueryValid() {
            if (this.inquiryReportSelected) {
                return Boolean(this.reportQuery.panelID)
            } else if (this.reportQuery.reportType == "timerPerformance") {
                return Boolean(this.reportQuery.timerID)
            }
            return true
        },
    },
    watch: {
        reportType: function (newValue, oldValue) {
            if (newValue !== oldValue) {
                this.reportIsLoaded = false
                this.reportQuery.panelID = null
            }
        },
    },
    methods: {
        // Fetch details about the link being viewed.
        fetchData() {
            let vm = this
            http.get(`/api/links/${this.linkId}/`).then((response) => {
                vm.shareLink = response.data
                vm.fetchCaseInfo()
            })
        },
        // Fetch report data.
        fetchReportData() {
            if (this.reportQuery.reportType == "caseCompletion") {
                this.$refs.completionReport.refresh()
            } else if (this.reportQuery.reportType == "choiceResponse") {
                this.$refs.choiceResponseReport.refresh()
            } else if (this.reportQuery.reportType == "textResponse") {
                this.$refs.textResponseReport.refresh()
            } else if (this.reportQuery.reportType == "timerPerformance") {
                this.$refs.timerPerformanceReport.refresh()
            } else if (this.reportQuery.reportType == "sessionList") {
                this.$refs.sessionListReport.refresh()
            }
        },
        fetchCaseInfo() {
            this.panelsLoading = true
            let panelTypeLookup = {
                choiceResponse: "multichoice,multiselect,singleselect,image_hotspots",
                textResponse: "textarea",
            }
            let panelType = panelTypeLookup[this.reportQuery.reportType]
            let vm = this
            http.get(`/api/reports/cases/${this.caseId}/`, {
                params: {
                    panelType: panelType,
                    activeOnly: true,
                },
            })
                .then(function (response) {
                    vm.panels = response.data.panels
                    vm.timers = response.data.node_timers
                })
                .finally(function () {
                    vm.panelsLoading = false
                })
        },
    },
    mounted() {
        this.fetchData()
    },
}
</script>
